import { Avatar, Box, Paper, Skeleton, Stack, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import * as R from "ramda";
import React, { useEffect, useRef, useState } from "react";

import { getAvatarColor } from "../../../common/colors";
import { parseTranscript, type TranscriptMessage } from "../../../common/transcript";
import CallRecordingPlayer from "../components/CallRecordingPlayer";
import useCallRecordings from "../hooks/useCallRecordings";

type TranscriptTabProps = {
  data: any | null;
  isLoading: boolean;
  initialTimestamp?: string | null;
};

/**
 * Parses a timestamp string in the format "HH:MM:SS" or "MM:SS" and returns the time in seconds
 */
const parseTimestampToSeconds = (timestamp: string): number | null => {
  // If there's no timestamp, return null
  if (!timestamp) {
    return null;
  }

  // Try to match timestamp (possible formats: "1:23:45", "01:23:45", "01:23", "1:23")
  const match = timestamp.match(/^(?:(\d+):)?(\d+):(\d+)$/);
  if (!match) {
    return null;
  }

  // Convert to seconds
  const [, hoursStr, minutesStr, secondsStr] = match;
  const hours = hoursStr ? parseInt(hoursStr, 10) : 0;
  const minutes = parseInt(minutesStr, 10);
  const seconds = parseInt(secondsStr, 10);

  return hours * 3600 + minutes * 60 + seconds;
};

/**
 * Renders a single message in the transcript
 */
type TranscriptMessageProps = {
  message: TranscriptMessage;
  onMessageClick?: (timestampSeconds: number | null) => void;
  isHighlighted?: boolean;
};

const TranscriptMessage = ({ message, onMessageClick, isHighlighted }: TranscriptMessageProps): React.ReactElement => {
  const { speaker, speakerInitials, timestamp, text } = message;
  const avatarColor = getAvatarColor(speaker);

  // Parse timestamp to seconds
  const timestampSeconds = parseTimestampToSeconds(timestamp);

  // Determine if this message is clickable (has a valid timestamp and click handler)
  const isClickable = Boolean(timestampSeconds !== null && onMessageClick);

  const handleClick = () => {
    if (isClickable && onMessageClick) {
      onMessageClick(timestampSeconds);
    }
  };

  return (
    <Box
      sx={{
        mb: 2,
        cursor: isClickable ? "pointer" : "default",
        backgroundColor: isHighlighted ? "rgba(25, 118, 210, 0.08)" : "transparent",
        borderRadius: "4px",
        padding: isHighlighted ? 1 : 0,
        transition: "background-color 0.3s ease"
      }}
      onClick={handleClick}
    >
      <Stack direction="row" spacing={2} alignItems="flex-start">
        <Avatar
          sx={{
            bgcolor: avatarColor.background,
            color: avatarColor.text,
            width: 40,
            height: 40,
            fontSize: 14,
            fontWeight: "bold"
          }}
        >
          {speakerInitials}
        </Avatar>
        <Box sx={{ flexGrow: 1 }}>
          <Stack direction="row" spacing={1} alignItems="baseline">
            <Typography variant="subtitle1" fontWeight="medium">
              {speaker}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {timestamp}
            </Typography>
            {isClickable && (
              <Typography variant="caption" color="primary" sx={{ fontStyle: "italic" }}>
                (Click to play)
              </Typography>
            )}
          </Stack>
          <Typography variant="body1" sx={{ mt: 0.5, whiteSpace: "pre-wrap" }}>
            {text}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
};

const LoadingSkeleton = (): React.ReactElement => (
  <Box sx={{ width: "100%" }}>
    <Skeleton variant="text" sx={{ fontSize: "2rem" }} />
    <Skeleton variant="rectangular" height={120} />
    <Skeleton variant="text" sx={{ fontSize: "1rem" }} />
    <Skeleton variant="text" sx={{ fontSize: "1rem" }} />
  </Box>
);

type TranscriptMessageListProps = {
  messages: TranscriptMessage[];
  onMessageClick?: (timestampSeconds: number | null) => void;
  currentTimeSeconds?: number;
};

const TranscriptMessageList = ({
  messages,
  onMessageClick,
  currentTimeSeconds
}: TranscriptMessageListProps): React.ReactElement => {
  const highlightedMessageRef = useRef<HTMLDivElement | null>(null);
  const previousHighlightedIndexRef = useRef<number | null>(null);

  const findCurrentMessage = () => {
    if (currentTimeSeconds === undefined) {
      return null;
    }

    let currentIndex = -1;

    for (let i = 0; i < messages.length; i++) {
      const timestamp = parseTimestampToSeconds(messages[i].timestamp);
      if (timestamp !== null && timestamp <= currentTimeSeconds) {
        currentIndex = i;
      } else if (timestamp !== null && timestamp > currentTimeSeconds && currentIndex !== -1) {
        break;
      }
    }

    return currentIndex;
  };

  const currentMessageIndex = findCurrentMessage();

  useEffect(() => {
    if (
      currentMessageIndex !== null &&
      currentMessageIndex !== -1 &&
      currentMessageIndex !== previousHighlightedIndexRef.current &&
      highlightedMessageRef.current
    ) {
      highlightedMessageRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center"
      });
      previousHighlightedIndexRef.current = currentMessageIndex;
    }
  }, [currentMessageIndex]);

  const renderMessage = (message: TranscriptMessage, index: number) => {
    const isHighlighted = currentMessageIndex === index;
    return (
      <Box
        key={`${index}-${message.speaker}-${message.timestamp}`}
        ref={isHighlighted ? highlightedMessageRef : null}
      >
        <TranscriptMessage
          message={message}
          onMessageClick={onMessageClick}
          isHighlighted={isHighlighted}
        />
      </Box>
    );
  };

  return (
    <Box sx={{ mt: 2 }}>
      {R.addIndex<TranscriptMessage, React.ReactElement>(R.map)(renderMessage, messages)}
    </Box>
  );
};

export const TranscriptTab = ({ data, isLoading, initialTimestamp }: TranscriptTabProps): React.ReactElement => {
  const transcriptText = R.pathOr("", ["transcript", "content", "transcription"], data);
  const summaryText = R.pathOr("", ["summary", "content", "summary"], data);
  const interactionId = R.pathOr(null, ["summary", "content", "interactionId"], data) ||
                        data?.transcript?.interaction_id;
  const messages = parseTranscript(transcriptText);
  const hasMessages = messages.length > 0;

  // Create a ref for the seek function that will be passed to the CallRecordingPlayer
  const seekToTimestampRef = useRef<((timestamp: number) => void) | null>(null);

  // Current time for highlighting the transcript
  const [currentTimeSeconds, setCurrentTimeSeconds] = useState<number | undefined>(undefined);

  // Track if we've already seeked to the initial timestamp
  const hasInitialSeekRef = useRef(false);

  // Check if account has Spoke API credentials
  const hasSpokeCredentials = R.pathOr(false, ["summary", "content", "accountHasSpokeCredentials"], data);

  // Fetch call recordings only if hasSpokeCredentials is true and we have an interactionId
  const {
    recordings,
    isLoading: recordingsLoading,
    error: recordingsError,
    refreshRecordings
  } = useCallRecordings(hasSpokeCredentials && interactionId ? interactionId : null);

  // Handle transcript message click
  const handleTranscriptMessageClick = (timestampSeconds: number | null) => {
    if (timestampSeconds !== null && seekToTimestampRef.current) {
      seekToTimestampRef.current(timestampSeconds);
      setCurrentTimeSeconds(timestampSeconds);
    }
  };

  // Handle audio player time update
  const handleTimeUpdate = (time: number) => {
    setCurrentTimeSeconds(time);
  };

  useEffect(() => {
    if (
      initialTimestamp &&
      !hasInitialSeekRef.current &&
      !isLoading
    ) {
      const timestampMs = parseInt(initialTimestamp, 10);
      if (!isNaN(timestampMs)) {
        const timestampSeconds = timestampMs / 1000;

        setCurrentTimeSeconds(timestampSeconds);

        if (seekToTimestampRef.current) {
          seekToTimestampRef.current(timestampSeconds);
        }

        hasInitialSeekRef.current = true;
      }
    }
  }, [initialTimestamp, isLoading]);

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  return (
    <Box>
      {/* Only render the call recording player if the account has Spoke credentials */}

      <Paper elevation={0} sx={{ p: 0, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 8 }}>
            {hasSpokeCredentials && (
              <CallRecordingPlayer
                recordings={recordings}
                isLoading={recordingsLoading}
                error={recordingsError}
                onRefresh={refreshRecordings}
                onSeekRef={seekToTimestampRef}
                onTimeUpdate={handleTimeUpdate}
                hasTranscript={hasMessages}
              />
            )}
            <Typography variant="subtitle1" fontWeight="bold" color="primary" sx={{ mb: 1, mt: 2 }}>
              Call transcript
              {hasSpokeCredentials && hasMessages && (
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  (Click on a message to play from that timestamp)
                </Typography>
              )}
            </Typography>

            {!hasMessages && (
              <Typography variant="body1" color="text.secondary">
                No transcript available for this call.
              </Typography>
            )}

            {hasMessages && (
              <TranscriptMessageList
                messages={messages}
                onMessageClick={hasSpokeCredentials ? handleTranscriptMessageClick : undefined}
                currentTimeSeconds={currentTimeSeconds}
              />
            )}
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Paper elevation={1} sx={{ p: 2, bgcolor: "background.default" }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Summary
              </Typography>
              <Typography variant="body1" paragraph>
                {summaryText}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default TranscriptTab;
